/**
 * Which surface a work item is showing in. Mirrors the placement's `openViewMode` /
 * `preferredViewMode`, where `SIDEBAR` is the preview panel.
 */
export type ViewSurface = 'MODAL' | 'SIDEBAR';

/** `arrows` covers both M2 cards: they share a heading and differ only in body copy and anchor. */
export type TourStepKind = 'intro' | 'arrows' | 'resize';

export type TourPlan = {
	/** The steps to run, in order. `length` is the counter's denominator. */
	steps: TourStepKind[];
	/**
	 * Which surface the `arrows` step was planned for, used only while nothing is open yet. Once a
	 * work item is showing, that step anchors in the live surface instead, and does not follow a
	 * later change to it.
	 */
	plannedArrowsSurface: ViewSurface;
};

export type DeriveTourPlanInput = {
	/** What is open at the tour's first render. Null when nothing is. */
	openViewMode: ViewSurface | null;
	/** The user's stored preference, i.e. what a work item will open in. */
	preferredViewMode: ViewSurface;
	/**
	 * Whether there is a list row to point the intro step at. False when the placement mounted in the
	 * panel header instead of the table, where no row is reachable.
	 */
	canAnchorToList: boolean;
};

/**
 * Derives the step sequence for one run of the GA changeboarding tour.
 *
 * Call this once, when the tour starts, and hold the result: both inputs are reported live, so
 * recomputing per render lets a visible "n of m" renumber under the user. The resulting over-promise
 * is accepted; renumbering is not.
 */
export const deriveTourPlan = ({
	openViewMode,
	preferredViewMode,
	canAnchorToList,
}: DeriveTourPlanInput): TourPlan => {
	// The intro anchors to a list row, so it cannot show once anything is open: a modal covers the
	// list, and an open panel makes the intro redundant.
	const hasIntro = openViewMode === null && canAnchorToList;

	const plannedArrowsSurface = openViewMode ?? preferredViewMode;

	// The resize step anchors to the panel's splitter, which a modal does not have. Expanding to a
	// modal mid-tour defers it rather than dropping it: it simply never finds its anchor.
	const hasResize = plannedArrowsSurface === 'SIDEBAR';

	const steps: TourStepKind[] = [];

	if (hasIntro) {
		steps.push('intro');
	}

	steps.push('arrows');

	if (hasResize) {
		steps.push('resize');
	}

	return { steps, plannedArrowsSurface };
};
